'use client';

import type { UnpackedUserToken } from '@/app_actions/Checkout/entities';
import { CookieService } from '@/app_lib/CookieService';
import logApiError from '@/main/lib/utils/log/logApiError';

export const unpackAndVerifyUserTokenClient = async (token: string) => {
  const response = await fetch('/api/verifyToken', {
    body: JSON.stringify({ token }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token verification failed: ${errorText}`);
  }

  const result = await response.json();

  if (result.error) {
    throw new Error(`Token verification error: ${result.error}`);
  }

  return result.decoded as UnpackedUserToken;
};

export const getIsLoggedInClient = async (): Promise<{
  data: null | UnpackedUserToken;
  pianoToken: string;
}> => {
  const pianoToken = CookieService.get('__utp');

  if (pianoToken) {
    try {
      const verifiedToken = await unpackAndVerifyUserTokenClient(pianoToken);
      if (verifiedToken.sub) {
        return { data: verifiedToken, pianoToken };
      }
      throw new Error('User token is invalid or missing sub');
    } catch (err) {
      // invalid token
      logApiError(err as Error, '[getIsLoggedIn] Invalid user token');
      return { data: null, pianoToken: '' };
    }
  }
  return { data: null, pianoToken: '' };
};

export const getUserAccessClient = async () => {
  try {
    let hasAccess = false;
    const { data: verifiedToken, pianoToken } = await getIsLoggedInClient();

    if (verifiedToken && pianoToken) {
      const accessResponse = await checkResourceAccessClient(verifiedToken.sub);
      hasAccess = Boolean(accessResponse?.access?.granted);
      return { data: verifiedToken, hasAccess, loggedIn: Boolean(verifiedToken.sub), pianoUserToken: pianoToken };
    }
  } catch (err) {
    // invalid token
    logApiError(err as Error, '[getUserAccess] Invalid user token');
  }
  return { data: null, hasAccess: false, loggedIn: false, pianoUserToken: '' };
};

export const checkResourceAccessClient = async (userId: string) => {
  try {
    const response = await fetch('/api/checkResourceAccess', {
      body: JSON.stringify({ userId }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Check resource access failed');
    }

    const result = await response.json();

    if (result.error) {
      throw new Error(`Check resource access error: ${result.error}`);
    }

    return result.accessResponse;
  } catch (err) {
    logApiError(err as Error, '[checkResourceAccessClient] Error checking resource access');
    return null;
  }
};
